import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { Icon } from './Icon.jsx';
import { dismissToast, subscribeToasts } from '../lib/toast.js';
import { cn } from '../lib/cn.js';

/**
 * The one toast listener, mounted once at the root. Bottom-right on desktop (so it never covers a
 * drawer's footer actions, which the prototype's bottom-centre toasts did), full-width on phones.
 * Info and success toasts leave after 5s; errors and toasts with an action stay until dismissed.
 */
const TONES = {
  success: { icon: CheckCircle2, color: 'text-success-dot' },
  info: { icon: Info, color: 'text-momo-blue-300' },
  error: { icon: AlertCircle, color: 'text-failed-dot' },
};

export function ToastViewport() {
  const [items, setItems] = useState([]);
  useEffect(() => subscribeToasts((ev) => {
    if (ev.type === 'add') {
      setItems((xs) => [...xs.slice(-3), ev.item]);
      if (!ev.item.sticky && ev.item.tone !== 'error') setTimeout(() => dismissToast(ev.item.id), 5000);
    } else if (ev.type === 'remove') {
      setItems((xs) => xs.filter((x) => x.id !== ev.id));
    }
  }), []);
  return createPortal(
    <div aria-live="polite" className="pointer-events-none fixed inset-x-3 bottom-3 z-toast flex flex-col items-stretch gap-2 sm:inset-x-auto sm:right-5 sm:bottom-5 sm:w-96">
      {items.map((t) => {
        const tone = TONES[t.tone] || TONES.info;
        return (
          <div key={t.id} role={t.tone === 'error' ? 'alert' : 'status'} className="pointer-events-auto flex animate-slide-in-right items-start gap-3 rounded-lg bg-ink-strong px-4 py-3 text-white shadow-toast">
            <Icon icon={tone.icon} size={18} className={cn('mt-px', tone.color)} />
            <div className="min-w-0 flex-1">
              <div className="text-base font-medium">{t.title}</div>
              {t.detail ? <div className="mt-0.5 text-sm text-nav-fg">{t.detail}</div> : null}
              {t.action ? (
                <button type="button" onClick={() => { dismissToast(t.id); t.action.onClick(); }} className="mt-2 text-sm font-bold text-mtn-yellow hover:underline">
                  {t.action.label}
                </button>
              ) : null}
            </div>
            <button type="button" aria-label="Dismiss" onClick={() => dismissToast(t.id)} className="-mr-1 -mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-nav-fg hover:bg-nav-hover hover:text-white">
              <Icon icon={X} size={14} />
            </button>
          </div>
        );
      })}
    </div>,
    document.body,
  );
}
